/**
 * A supporter's basket, held in the browser.
 *
 * Not on the server, because a server-side basket means a row per abandoned
 * browse and stock held for people who were only looking. The order — and the
 * stock hold — is created once, at the moment somebody commits to paying.
 *
 * Nothing about money is stored here. Lines carry a price only so the basket
 * can show a running total; every figure that matters is recalculated by the
 * server from product ids at Quote and again at CreateOrder. A fan editing
 * localStorage changes what their own screen says and nothing else.
 *
 * Keyed by club slug so two clubs open in two tabs don't merge baskets.
 */

export type BasketLine = {
	productType: number;
	productId: number;
	ticketTypeId: number;
	quantity: number;
	/** Display only — the server prices the order. */
	name: string;
	pricePence: number;
	occasion: string;
	when: string;
	/** Who each seat is for. Season tickets only. */
	holders?: { name: string; email: string | null; dateOfBirth: string | null }[];
	/** Reserved seats, where the club sells them. These ARE the quantity. */
	seatIds?: number[];
};

const key = (slug: string) => `pb-basket-${slug}`;

/** Same line means same product AND same ticket type — a concession isn't an adult. */
const same = (a: BasketLine, b: BasketLine) =>
	a.productType === b.productType &&
	a.productId === b.productId &&
	a.ticketTypeId === b.ticketTypeId &&
	// A line naming its holders or its seats is never merged. Two season
	// tickets for two different people, or two lines of specific seats, are
	// separate — adding their quantities would throw one set away.
	!a.holders && !b.holders && !a.seatIds && !b.seatIds;

export const Basket = {
	read(slug: string): BasketLine[] {
		try {
			const raw = window.localStorage.getItem(key(slug));
			const parsed = raw ? JSON.parse(raw) : [];
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			// Corrupt or unavailable storage shouldn't take the shop down.
			return [];
		}
	},

	write(slug: string, lines: BasketLine[]) {
		try {
			window.localStorage.setItem(key(slug), JSON.stringify(lines));
		} catch {
			// Private browsing. The basket survives the page, not the session.
		}
	},

	add(slug: string, line: BasketLine) {
		const lines = Basket.read(slug);
		const existing = lines.find((l) => same(l, line));
		if (existing) {
			existing.quantity += line.quantity;
		} else {
			lines.push(line);
		}
		Basket.write(slug, lines);
		return lines;
	},

	setQuantity(slug: string, index: number, quantity: number) {
		const lines = Basket.read(slug);
		if (!lines[index]) return lines;
		if (quantity < 1) {
			lines.splice(index, 1);
		} else {
			lines[index].quantity = quantity;
		}
		Basket.write(slug, lines);
		return lines;
	},

	clear(slug: string) {
		Basket.write(slug, []);
	},

	count(slug: string) {
		return Basket.read(slug).reduce((total, l) => total + l.quantity, 0);
	},

	/** Indicative only. The total a fan is charged comes back from Quote. */
	subtotalPence(lines: BasketLine[]) {
		return lines.reduce((total, l) => total + l.pricePence * l.quantity, 0);
	}
};

/** Product types, mirroring OrderProductTypeEnum on the API. */
export const ProductType = {
	Fixture: 0,
	Event: 10,
	SeasonTicket: 20,
	Membership: 30,
	Hospitality: 40
};

export const money = (pence: any) =>
	`£${((Number(pence) || 0) / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
